import React, { useCallback, useEffect, useMemo } from 'react'
import DropdownGender from './DropdownGender'
import DropdownCountries from './DropdownCountries'
import styles from './styles/player-filter-bar.module.scss'
import genericStyles from './styles/dropdown.module.scss'
import { setFilters, setSorting } from 'actions/search-actions'
import { resetFilters } from 'actions/search-actions'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import useWindowSize from 'hooks/use-windowSize'
import { TABLET_BREAKPOINT } from 'constants/global'
import MobileFilter from 'components/generic/MobileFilterSelector/MobileFilterSelector'
import { GenderFilter, Option, RatingFilter, Sorting, SortingField } from 'types/global'
import MainContainer from 'components/generic/MainContainer'
import cx from 'classnames'
import DropdownAgeGroups from './DropdownAgeGroups'
import useScrolledPast from 'hooks/use-scrolled-past'
import useElement from 'hooks/use-element'
import { createPortal } from 'react-dom'
import { ClassNamesProps } from 'types/utilities'
import {
  AGE_GROUPS,
  DEFAULT_PLAYER_SEARCH_FILTERS,
  DEFAULT_TABLE_SORTING,
} from 'constants/player-search'
import DropdownRatingCombined from './DropdownRatingsCombined'
import { PostScoreModalId } from 'constants/modals'
import { SearchFilters } from 'reducers/search-reducer'
import useFiltersCount from 'hooks/use-filters-count'
import { useTranslation } from 'react-i18next'
import { useGameZone } from 'hooks/use-gamezone'
import { RatingTypeEnum } from 'types/api/enums'
import { transformGameZoneValues } from './CombinedRatingFilter/utils'

export const DEFAULT_MIN_RANGE = 1
export const DEFAULT_MAX_RANGE = 40
export const DEFAULT_STEP_SIZE = 0.1

type ClassKeys = 'wrapper' | 'sectionContainer' | 'desktopFiltersContainer'

interface StickyBehaviourConfig {
  zIndex: number
  shouldPortal: boolean
  pinToNavbar: boolean
  offsetTop: number
}

interface Props extends ClassNamesProps<ClassKeys> {
  searchId: string
  minRange?: number
  maxRange?: number
  stepSize?: number
  labelDesktop?: string
  stickyBehaviour?: StickyBehaviourConfig | false
  applyFiltersOnMount?: boolean
}

const FilterBar = ({
  searchId,
  minRange = DEFAULT_MIN_RANGE,
  maxRange = DEFAULT_MAX_RANGE,
  stepSize = DEFAULT_STEP_SIZE,
  labelDesktop,
  stickyBehaviour = { zIndex: 2, shouldPortal: false, pinToNavbar: true, offsetTop: 0 },
  classNames,
  applyFiltersOnMount = false,
}: Props) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['strFilters', 'strMobileFilter'])
  const rating = useSelector((state: AppState) => state.searchbar.filters[searchId].rating)
  const selectedGender = useSelector((state: AppState) => state.searchbar.filters[searchId].gender)
  const countries = useSelector((state: AppState) => state.searchbar.filters[searchId].countries)
  const sorting = useSelector((state: AppState) => state.searchbar.sorting[searchId]?.field)
  const searchQuery = useSelector((state: AppState) => state.searchbar.searchQuery[searchId])
  const { combinedAppliedCount, filtersAppliedCount } = useFiltersCount(searchId)
  const gameZone = useGameZone()
  const genderOptions = useMemo(
    () => [
      { label: t('anyGenderOption', { ns: 'strFilters' }), value: GenderFilter.ANY },
      { label: t('female', { ns: 'strFilters' }), value: GenderFilter.FEMALE },
      { label: t('male', { ns: 'strFilters' }), value: GenderFilter.MALE },
    ],
    [t]
  )
  const ageGroupOptionsMobile: Option[] = useMemo(
    () => [{ value: '', label: t('anyAgeGroupOption', { ns: 'strMobileFilter' }) }, ...AGE_GROUPS],
    [t]
  )
  const sortingItems: { value: SortingField; label: string }[] = useMemo(
    () => [
      { value: SortingField.FamilyName, label: t('sortingLabelName', { ns: 'strMobileFilter' }) },
      {
        value: SortingField.WTNSingles,
        label: t('sortingLabelSingles', { ns: 'strMobileFilter' }),
      },
      {
        value: SortingField.WTNDoubles,
        label: t('sortingLabelDoubles', { ns: 'strMobileFilter' }),
      },
    ],
    [t]
  )

  const filtersOnMount: Record<string, SearchFilters> = useMemo(() => {
    const gameZoneSingles = gameZone?.[RatingTypeEnum.ITF]
    const defaltPlayerSearchFilters = {
      ...DEFAULT_PLAYER_SEARCH_FILTERS,
      rating: {
        ...DEFAULT_PLAYER_SEARCH_FILTERS.rating,
        gameZone: gameZoneSingles && transformGameZoneValues(gameZoneSingles),
      },
    }
    const FILTERS_ON_MOUNT = {
      'player-search': defaltPlayerSearchFilters,
      'player-search-watchlist': {},
      [PostScoreModalId.ADD_PLAYER]: {},
    }

    return FILTERS_ON_MOUNT[searchId] ?? {}
  }, [searchId, gameZone])

  const resetGlobalFilters = useCallback(
    () => dispatch(resetFilters(searchId)),
    [searchId, dispatch]
  )
  const setRating = useCallback(
    (rating: RatingFilter | null) =>
      dispatch(
        setFilters(
          {
            rating,
          },
          searchId
        )
      ),
    [dispatch, searchId]
  )

  const setGenderFilter = useCallback(
    gender => dispatch(setFilters({ gender: gender }, searchId)),
    [dispatch, searchId]
  )
  const setSelectedCountries = useCallback(
    codesArray => dispatch(setFilters({ countries: codesArray }, searchId)),
    [dispatch, searchId]
  )
  const setGlobalSorting = useCallback(
    (sorting: Sorting | null) => {
      dispatch(setSorting(sorting, searchId))
    },
    [dispatch, searchId]
  )

  const filtersPopulated = !!filtersAppliedCount
  const reset = () => filtersPopulated && resetGlobalFilters()
  const windowWidth = useWindowSize().width
  const ageGroup = useSelector((state: AppState) => state.searchbar.filters[searchId].ageGroup)
  const setAgeGroupFilter = useCallback(
    (ageGroup?: string) => {
      dispatch(
        setFilters(
          {
            ageGroup: ageGroup,
          },
          searchId
        )
      )
    },
    [dispatch, searchId]
  )

  const [anchor, anchorRef] = useElement<HTMLDivElement>()
  const [filtersSection, filtersSectionRef] = useElement()
  const shouldBeFixed = useScrolledPast(stickyBehaviour ? anchor : null) && !!stickyBehaviour

  useEffect(() => {
    if (applyFiltersOnMount) {
      dispatch(setFilters(filtersOnMount, searchId))
    }
  }, [dispatch, searchId, applyFiltersOnMount, filtersOnMount])

  const FiltersSection = (
    <section
      ref={filtersSectionRef}
      className={cx(styles.filterBarSection, classNames?.sectionContainer, {
        [styles.isFixed]: shouldBeFixed,
        [styles.isPinnedToNavbar]: shouldBeFixed && stickyBehaviour.pinToNavbar,
      })}
      style={
        shouldBeFixed
          ? { zIndex: stickyBehaviour.zIndex, top: stickyBehaviour.offsetTop }
          : undefined
      }
    >
      {windowWidth && windowWidth > TABLET_BREAKPOINT ? (
        <>
          {labelDesktop && <h2 className={styles.filterBarHeader}>{labelDesktop}</h2>}

          <div className={cx(styles.filterBar, classNames?.desktopFiltersContainer)}>
            <DropdownGender
              options={genderOptions}
              selectedGender={selectedGender ?? GenderFilter.ANY}
              setGender={setGenderFilter}
            />

            <DropdownAgeGroups
              className={genericStyles.dropdown}
              value={ageGroup ?? ''}
              setValue={setAgeGroupFilter}
            />

            <DropdownCountries
              filterCountries={countries}
              setCountries={setSelectedCountries}
              defaultLabel={t('countryDefaultLabel', { ns: 'strFilters' })}
            />

            <DropdownRatingCombined
              id={'wtn-rating'}
              rating={rating ?? null}
              defaultLabel={t('rating.labelDropdownCombined', { ns: 'strFilters' })}
              appliedRangeLabel={t('rating.labelDropdownCombined', { ns: 'strFilters' })}
              setRating={setRating}
              minRange={minRange}
              maxRange={maxRange}
              stepSize={stepSize}
            />

            {filtersPopulated && (
              <button onClick={reset} className={genericStyles.resetFilters}>
                {t('clearFilters')}
              </button>
            )}
          </div>
        </>
      ) : (
        <MobileFilter
          appliedFiltersAndSortCount={combinedAppliedCount}
          classNames={{ filterBar: styles.filterBarMobile }}
          sortingSelectionData={{
            defaultSorting: !searchQuery ? DEFAULT_TABLE_SORTING : null,
            selectedSorting: sorting,
            sortingSelectionItems: sortingItems,
            setSorting: setGlobalSorting,
            resetFilters: reset,
          }}
          genderFilterData={{
            genderItems: genderOptions,
            selectedGenderItem: selectedGender ?? GenderFilter.ANY,
            genderDispatch: setGenderFilter,
            resetFilters: reset,
          }}
          ageGroupFilterData={{
            ageGroupItems: ageGroupOptionsMobile,
            ageGroupDispatch: setAgeGroupFilter,
            selectedAgeGroup: ageGroup,
          }}
          countriesFilterData={{
            searchId,
            selectedCountries: countries,
            countriesDispatch: setSelectedCountries,
          }}
          ratingRangesFilterData={{
            minRange,
            maxRange,
            stepSize,
            selectedRating: rating ?? null,
            setRating,
            resetFilters: reset,
          }}
          resetGlobalFilters={reset}
        />
      )}
    </section>
  )

  return (
    <MainContainer
      ref={anchorRef}
      className={classNames?.wrapper}
      style={
        filtersSection && shouldBeFixed ? { height: `${filtersSection.offsetHeight}px` } : undefined
      }
    >
      {shouldBeFixed && stickyBehaviour.shouldPortal
        ? createPortal(FiltersSection, document.body)
        : FiltersSection}
    </MainContainer>
  )
}

export default FilterBar
